export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#f7f7f4] py-12 px-4">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8">
          <a href="/" className="text-sm text-neutral-500 hover:text-neutral-800">
            ← Back to AusProp
          </a>
        </div>

        <div className="rounded-2xl border border-neutral-200 bg-white p-8 md:p-12 space-y-8">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Privacy Policy</h1>
            <p className="mt-2 text-sm text-neutral-500">Last updated: April 2026</p>
          </div>

          <p className="text-sm text-neutral-600 leading-relaxed">
            This Privacy Policy explains how AusProp (ABN 37 317 085 485) collects, uses, and
            protects your personal information in accordance with the{" "}
            <em>Privacy Act 1988</em> (Cth) and the Australian Privacy Principles.
          </p>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-neutral-800">1. What we collect</h2>
            <p className="text-sm text-neutral-600 leading-relaxed">
              We collect the following information when you use AusProp:
            </p>
            <ul className="space-y-2 text-sm text-neutral-600 list-disc list-inside leading-relaxed">
              <li>
                <strong>Account information:</strong> your email address and password (hashed — we
                never see your plain-text password).
              </li>
              <li>
                <strong>Property and financial data:</strong> property addresses, values, loan
                balances, rental income, expenses, and any other data you choose to enter into the app.
              </li>
              <li>
                <strong>Profile information:</strong> name, income, tax settings, and goals you
                optionally provide to enable calculations and strategy features.
              </li>
              <li>
                <strong>Usage data:</strong> basic app usage for debugging and improving the service.
                We do not use third-party analytics trackers.
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-neutral-800">2. How we store your data</h2>
            <p className="text-sm text-neutral-600 leading-relaxed">
              Your data is stored on <strong>Supabase</strong>, a database platform hosted on AWS
              infrastructure in Australia and the United States. Supabase is SOC 2 Type II certified.
              All data is encrypted at rest using AES-256 and encrypted in transit using TLS 1.2+.
            </p>
            <p className="text-sm text-neutral-600 leading-relaxed">
              Access to your data is controlled by row-level security policies — only your account
              can read or write your data. AusProp staff do not access your data unless required
              to diagnose a technical issue you have reported.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-neutral-800">3. Who we share data with</h2>
            <p className="text-sm text-neutral-600 leading-relaxed">
              We do not sell, rent, or share your personal information with any third party for
              marketing purposes. We share data with third parties only as follows:
            </p>
            <ul className="space-y-2 text-sm text-neutral-600 list-disc list-inside leading-relaxed">
              <li>
                <strong>Anthropic (AI features):</strong> when you use AI features (chat, import
                wizard, strategy narrative), a summary of your portfolio data is sent to Anthropic's
                API to generate responses. Anthropic does not store this data or use it to train
                models. This applies only to paid Investor plan users.
              </li>
              <li>
                <strong>Stripe (payments):</strong> subscription billing is handled by Stripe. We
                never see or store your card details. Stripe stores payment information in accordance
                with PCI DSS standards.
              </li>
              <li>
                <strong>Domain API (suburb data):</strong> your property's suburb, state, and
                postcode are sent to Domain's API to retrieve suburb market data. No personally
                identifiable information is included.
              </li>
            </ul>
            <p className="text-sm text-neutral-600 leading-relaxed">
              We may disclose information if required to do so by law or court order.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-neutral-800">4. Cookies and session data</h2>
            <p className="text-sm text-neutral-600 leading-relaxed">
              AusProp does not use advertising cookies or tracking pixels. We use a single
              authentication session cookie set by Supabase to keep you logged in. This cookie is
              strictly necessary for the app to function and does not track you across other websites.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-neutral-800">5. Your rights</h2>
            <p className="text-sm text-neutral-600 leading-relaxed">
              Under the Australian Privacy Principles, you have the right to:
            </p>
            <ul className="space-y-2 text-sm text-neutral-600 list-disc list-inside leading-relaxed">
              <li>Access the personal information we hold about you.</li>
              <li>Request correction of inaccurate information.</li>
              <li>Request deletion of your account and all associated data.</li>
              <li>Make a complaint about how we handle your personal information.</li>
            </ul>
            <p className="text-sm text-neutral-600 leading-relaxed">
              To exercise any of these rights, email us at{" "}
              <a href="mailto:propertyoshq@gmail.com" className="underline text-neutral-900 hover:text-neutral-600">
                propertyoshq@gmail.com
              </a>
              . We will respond within 30 days.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-neutral-800">6. Data retention</h2>
            <p className="text-sm text-neutral-600 leading-relaxed">
              We retain your data for as long as your account is active. If you cancel your
              subscription, your data remains accessible on the free tier. If you request account
              deletion, we will permanently delete all your data within 30 days.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-neutral-800">7. Changes to this policy</h2>
            <p className="text-sm text-neutral-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of material
              changes via email. The date at the top of this page reflects the most recent update.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-neutral-800">8. Governing law and complaints</h2>
            <p className="text-sm text-neutral-600 leading-relaxed">
              This policy is governed by the laws of Victoria, Australia. If you believe we have
              not handled your personal information appropriately, you may contact the Office of the
              Australian Information Commissioner (OAIC) at{" "}
              <a
                href="https://www.oaic.gov.au"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-neutral-900 hover:text-neutral-600"
              >
                oaic.gov.au
              </a>
              .
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-neutral-800">9. Contact</h2>
            <p className="text-sm text-neutral-600 leading-relaxed">
              Privacy enquiries:{" "}
              <a href="mailto:propertyoshq@gmail.com" className="underline text-neutral-900 hover:text-neutral-600">
                propertyoshq@gmail.com
              </a>
            </p>
            <p className="text-sm text-neutral-600">ABN: 37 317 085 485</p>
          </section>
        </div>
      </div>
    </div>
  );
}
